import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Snackbar,
  SnackbarContent,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import useBatchSchedules from "../hooks/useBatchSchedules";
import dayjs from "dayjs";
import {
  Close,
  Delete,
  Edit,
  ExpandLessOutlined,
  ExpandMoreOutlined,
} from "@mui/icons-material";
import { errorColor, successColor } from "../../sdk";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";

function formatTimeToHHMM(hour, minute) {
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  return `${hh}:${mm}`;
}

const batchHeadings = [
  {
    key: "name",
    label: "Name",
    style: { width: "25%" },
  },
  {
    key: "minTime",
    label: "Min Time",
    style: { width: "12.5%", textAlign: "center" },
  },
  {
    key: "maxTime",
    label: "Max Time",
    style: { width: "12.5%", textAlign: "center" },
  },
  {
    key: "schedules",
    label: "Schedules",
    style: { width: "25%", textAlign: "center" },
  },
  {
    key: "actions",
    label: "Actions",
    style: { width: "25%" },
  },
];

const scheduleHeadings = [
  {
    key: "rateshopName",
    label: "RateShop Name",
    style: { width: "30%" },
  },
  {
    key: "scheduleTime",
    label: "Schedule Time",
    style: { width: "15%", textAlign: "center" },
  },
  {
    key: "emails",
    label: "Emails",
    style: { width: "30%" },
  },
  {
    key: "scheduleActions",
    label: "Actions",
    style: { width: "20%", textAlign: "center" },
  },
];

const BatchSchedules = () => {
  const {
    batches,
    schedules,
    remainingSchedules,
    hourlyOptions,
    addNewBatch,
    setAddNewBatch,
    newBatchDetails,
    handleAddSchedulesInBatch,
    handleCreateNewBatch,
    handleDeleteBatch,
    handleRemoveSchedulesFromBatch,
    handleChangeNewBatchDetails,
    isBatchesLoading,
    showSnackbarMsg,
    setShowSnackbarMsg,
    handleChangeBatchDetails,
    handleUpdateBatchDetails,
    showConfirmationDialog,
    setShowConfirmationDialog,
    handleCloseConfirmationDialog,
  } = useBatchSchedules();

  if (isBatchesLoading) {
    return (
      <ScreenWrapper>
        <CircularProgress />
      </ScreenWrapper>
    );
  }

  return (
    <BatchSchedulesWrapper>
      <Stack className="header">
        <Typography className="header-text">Batch Schedules</Typography>

        <Button
          variant="contained"
          onClick={() => setAddNewBatch(!addNewBatch)}
        >
          + Create Batch
        </Button>
      </Stack>

      <Stack className="batch-schedules-box">
        <Stack className="batch-header">
          {batchHeadings.map((item) => (
            <Typography
              className="batch-header-text"
              key={item?.key}
              sx={item?.style}
            >
              {item?.label}
            </Typography>
          ))}
        </Stack>
        <Stack className="batch-body">
          {addNewBatch && (
            <Stack className="new-batch">
              <Stack className="new-batch-left">
                <Stack sx={{ width: "50%" }}>
                  <TextField
                    label="Name"
                    value={newBatchDetails?.name}
                    onChange={(event) =>
                      handleChangeNewBatchDetails(event.target.value, "name")
                    }
                    sx={{ width: "100%" }}
                  />
                </Stack>

                <Stack alignItems={"center"} sx={{ width: "25%" }}>
                  <FormControl id="min-time">
                    <InputLabel>Min Time</InputLabel>
                    <Select
                      labelId="min-time"
                      value={
                        newBatchDetails?.minTime
                          ? dayjs(newBatchDetails?.minTime).format("HH:mm")
                          : ""
                      }
                      label="Min Time"
                      onChange={(e) =>
                        handleChangeNewBatchDetails(
                          dayjs(e.target.value, "HH:mm").toDate(),
                          "minTime"
                        )
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      }}
                    >
                      {hourlyOptions.map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack alignItems={"center"} sx={{ width: "25%" }}>
                  <FormControl id="max-time">
                    <InputLabel>Max Time</InputLabel>
                    <Select
                      labelId="max-time"
                      value={
                        newBatchDetails?.maxTime
                          ? dayjs(newBatchDetails?.maxTime).format("HH:mm")
                          : ""
                      }
                      label="Max Time"
                      onChange={(e) =>
                        handleChangeNewBatchDetails(
                          dayjs(e.target.value, "HH:mm").toDate(),
                          "maxTime"
                        )
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      }}
                    >
                      {hourlyOptions.map((time) => (
                        <MenuItem key={time} value={time}>
                          {time}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              <Stack className="new-batch-right">
                <Button
                  variant="contained"
                  disabled={!newBatchDetails?.name}
                  onClick={handleCreateNewBatch}
                >
                  Save
                </Button>
                <IconButton onClick={() => setAddNewBatch(false)}>
                  <Close />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Stack>
        {batches?.map((batch, index) => {
          const schedulesList =
            schedules?.filter((item) =>
              batch?.scheduleIds?.includes(item?.scheduledId)
            ) || [];

          return (
            <BatchAccordian
              key={batch?.id}
              batch={batch}
              hourlyOptions={hourlyOptions}
              schedulesList={schedulesList}
              setShowConfirmationDialog={setShowConfirmationDialog}
              remainingSchedules={remainingSchedules}
              handleChangeBatchDetails={handleChangeBatchDetails}
              handleUpdateBatchDetails={handleUpdateBatchDetails}
              handleAddSchedulesInBatch={handleAddSchedulesInBatch}
            />
          );
        })}
      </Stack>

      <StyledDialog
        open={showConfirmationDialog?.open}
        onClose={handleCloseConfirmationDialog}
        PaperProps={{
          sx: {
            width: "350px",
          },
        }}
      >
        <DialogTitle className="title">
          <Stack direction="row" alignItems="center" gap="8px">
            <Typography fontWeight="600" fontSize="24px">
              Warning
            </Typography>
            <WarningIcon sx={{ color: "#a90f0f" }} />
          </Stack>
        </DialogTitle>

        <CloseIcon
          className="closeButton"
          onClick={handleCloseConfirmationDialog}
        />
        <DialogContent className="content">
          <Typography textAlign="center">
            Are you sure you want to delete this ?
          </Typography>
          <Stack direction="row" gap="40px" padding="24px">
            <Button variant="contained" onClick={handleCloseConfirmationDialog}>
              No
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (showConfirmationDialog?.isBatchDelete) {
                  handleDeleteBatch(showConfirmationDialog?.batchId);
                } else {
                  handleRemoveSchedulesFromBatch(
                    showConfirmationDialog?.batchId,
                    showConfirmationDialog?.scheduleId
                  );
                }
              }}
            >
              Yes
            </Button>
          </Stack>
        </DialogContent>
      </StyledDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={showSnackbarMsg?.open}
        autoHideDuration={6000}
        onClose={() => {
          setShowSnackbarMsg({
            ...showSnackbarMsg,
            open: false,
          });
        }}
      >
        <SnackbarContent
          sx={{
            backgroundColor: showSnackbarMsg?.isError
              ? errorColor
              : successColor,
            textTransform: "capitalize",
          }}
          message={showSnackbarMsg?.message}
        />
      </Snackbar>
    </BatchSchedulesWrapper>
  );
};

export default BatchSchedules;

const BatchAccordian = ({
  batch,
  schedulesList,
  hourlyOptions,
  setShowConfirmationDialog,
  remainingSchedules,
  handleChangeBatchDetails,
  handleUpdateBatchDetails,
  handleAddSchedulesInBatch,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedSchedules, setSelectedSchedules] = useState([]);

  const handleChangeAccordion = () => {
    if (!editMode) setExpanded(!expanded);
  };

  const handleChangeSelectSchedules = (event) => {
    const { value } = event.target;
    setSelectedSchedules(value);
  };

  const handleEditMode = (event) => {
    event.stopPropagation();
    setExpanded(false);
    setEditMode(!editMode);
  };

  return (
    <Accordion expanded={expanded} onChange={handleChangeAccordion}>
      <AccordionSummary
        sx={{
          minHeight: expanded ? "54px !important" : "64px",
          "& .MuiAccordionSummary-content": {
            margin: 0,
            minHeight: expanded ? "30px" : "56px",
          },
        }}
      >
        <Stack className="batches-row">
          {editMode ? (
            <TextField
              label="Name"
              value={batch?.name}
              onChange={(event) =>
                handleChangeBatchDetails(batch?.id, "name", event.target.value)
              }
              sx={{ width: "25%" }}
            />
          ) : (
            <Typography sx={{ width: "25%" }}>{batch?.name}</Typography>
          )}

          {editMode ? (
            <Stack alignItems={"center"} sx={{ width: "12.5%" }}>
              <FormControl id="min-time">
                <InputLabel>Min Time</InputLabel>
                <Select
                  labelId="min-time"
                  value={
                    batch?.minTime ? dayjs(batch?.minTime).format("HH:mm") : ""
                  }
                  label="Min Time"
                  onChange={(e) =>
                    handleChangeBatchDetails(
                      batch?.id,
                      "minTime",
                      dayjs(e.target.value, "HH:mm").toDate()
                    )
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {hourlyOptions.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          ) : (
            <Typography sx={{ width: "12.5%", textAlign: "center" }}>
              {dayjs(batch?.minTime).format("HH:mm")}
            </Typography>
          )}

          {editMode ? (
            <Stack alignItems={"center"} sx={{ width: "12.5%" }}>
              <FormControl id="max-time">
                <InputLabel>Max Time</InputLabel>
                <Select
                  labelId="max-time"
                  value={
                    batch?.maxTime ? dayjs(batch?.maxTime).format("HH:mm") : ""
                  }
                  label="Max Time"
                  onChange={(e) =>
                    handleChangeBatchDetails(
                      batch?.id,
                      "maxTime",
                      dayjs(e.target.value, "HH:mm").toDate()
                    )
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {hourlyOptions.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          ) : (
            <Typography sx={{ width: "12.5%", textAlign: "center" }}>
              {dayjs(batch?.maxTime).format("HH:mm")}
            </Typography>
          )}

          <Stack width="25%" justifyContent={"center"} alignItems={"center"}>
            {!editMode && (
              <Typography className="schedule-length-text">
                {schedulesList?.length ?? 0}
              </Typography>
            )}
          </Stack>

          <Stack className="action-buttons" width={"25%"}>
            <Stack direction={"row"} alignItems={"center"}>
              {editMode && (
                <Button
                  onClick={() => handleUpdateBatchDetails(batch)}
                  variant="contained"
                >
                  Save
                </Button>
              )}
              <IconButton onClick={(event) => handleEditMode(event)}>
                {editMode ? <Close /> : <Edit />}
              </IconButton>
              {!editMode && (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowConfirmationDialog((prev) => ({
                      ...prev,
                      open: true,
                      isBatchDelete: true,
                      batchId: batch?.id,
                    }));
                  }}
                >
                  <Delete sx={{ color: "#306FBC" }} />
                </IconButton>
              )}
            </Stack>
            {expanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
            <Stack></Stack>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack className="schedule-header">
          {scheduleHeadings.map((item) => (
            <Typography
              className="batch-header-text"
              key={item?.key}
              sx={item?.style}
            >
              {item?.label}
            </Typography>
          ))}
        </Stack>
        <Stack className="schedules-body">
          <Stack className="scrollable-schedules">
            {schedulesList?.map((schedule) => (
              <Stack key={schedule?.id} className="schedule-item">
                <Typography sx={{ width: "30%" }}>
                  {schedule?.rateShopName}
                </Typography>

                <Stack justifyContent="center" alignItems="center" width="15%">
                  <Typography>
                    {formatTimeToHHMM(schedule?.hour, schedule?.minute)}
                  </Typography>
                </Stack>

                <Stack width="30%">
                  <Tooltip title={schedule?.emails?.join(",")}>
                    <Typography
                      sx={{
                        width: "94%",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {schedule?.emails.join(",")}
                    </Typography>
                  </Tooltip>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ width: "20%" }}
                >
                  <IconButton
                    onClick={() =>
                      setShowConfirmationDialog((prev) => ({
                        open: true,
                        isBatchDelete: false,
                        batchId: batch?.id,
                        scheduleId: schedule?.scheduledId,
                      }))
                    }
                    sx={{ color: "#306FBC" }}
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              </Stack>
            ))}
          </Stack>

          {/* New schedule section */}
          <Stack className="new-schedules" mt={1}>
            <FormControl sx={{ width: "30%" }}>
              <InputLabel id="multiple-schedules-checkbox-label">
                Select Schedules to Add
              </InputLabel>
              <Select
                labelId="multiple-schedules-checkbox-label"
                multiple
                value={selectedSchedules}
                input={<OutlinedInput label="Select Schedules to Add" />}
                onChange={(event) => handleChangeSelectSchedules(event)}
                renderValue={(selected) => {
                  const rateShopNames = selected
                    .map((item) => item.rateShopName)
                    .join(", ");
                  return rateShopNames;
                }}
              >
                {remainingSchedules?.map((item) => (
                  <MenuItem key={item?.scheduledId} value={item}>
                    <Stack
                      direction="row"
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      width="100%"
                    >
                      <Stack direction="row" alignItems={"center"} spacing={2}>
                        <Checkbox checked={selectedSchedules.includes(item)} />
                        <ListItemText primary={`${item?.rateShopName}`} />
                      </Stack>
                      <Stack>
                        <ListItemText
                          primary={`${formatTimeToHHMM(
                            item?.hour,
                            item?.minute
                          )}`}
                        />
                      </Stack>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              onClick={() =>
                handleAddSchedulesInBatch(batch?.id, selectedSchedules)
              }
              variant="contained"
              disabled={!selectedSchedules?.length}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// Styled Components
const ScreenWrapper = styled(Stack)(() => ({
  width: "100%",
  height: "calc(100vh - 160px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const BatchSchedulesWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  height: "calc(100vh - 160px)",
  // overflowY: "scroll",
  overflowX: "hidden",
  ".header": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(2),
    justifyContent: "space-between",
  },
  ".header-text": {
    fontSize: theme.spacing(2),
    fontWeight: 700,
  },
  ".batch-schedules-box": {
    backgroundColor: "#ffffff",
    borderRadius: theme.spacing(1),
    boxShadow: "1px 4px 10px rgba(48, 111, 188, 0.2)",
    padding: theme.spacing(5),
  },
  ".batch-header": {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    backgroundColor: "#306FBC",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    color: "#ffffff",
  },
  ".batch-header-text": {
    fontWeight: 700,
  },
  ".batch-body": {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  ".new-batch": {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    border: `1px solid #E5E7EB`,
  },
  ".new-batch-left": {
    width: "50%",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    gap: theme.spacing(2),
  },
  ".new-batch-right": {
    width: "24%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(2),
  },
  ".action-buttons": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    gap: theme.spacing(2),
  },
  ".batches-row": {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    gap: theme.spacing(2),
  },
  ".schedule-header": {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "row",
    backgroundColor: "#599bd4",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    paddingRight: theme.spacing(3),
    color: "#ffffff",
  },
  ".scrollable-schedules": {
    maxHeight: "200px",
    overflowY: "auto",
    paddingRight: "8px",
  },
  ".schedules-body": {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  ".schedule-item": {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
    justifyContent: "space-between",
    borderBottom: `1px solid #E5E7EB`,
  },
  ".new-schedules": {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    gap: theme.spacing(5),
    padding: theme.spacing(1),
  },
  ".schedule-length-text": {
    padding: "5px 10px",
    borderRadius: "10px",
    backgroundColor: "#DBE1FD",
    color: "#6763B9",
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  ".closeButton": {
    position: "absolute",
    right: "10px",
    top: "10px",
    cursor: "pointer",
  },
  ".title": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  ".content": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: `0px ${theme.spacing(2)}`,
  },
}));
